import { useState, FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FlaskConical, Loader2 } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { azureAuthService } from '@/services/azureAuthService';
import Watermark from '@/components/ui/Watermark';
import CopyrightFooter from '@/components/ui/CopyrightFooter';

export default function LoginPage() {
  const navigate = useNavigate();
  const { loginWithEmail, loginWithGoogle, loginWithMicrosoft, isLoading } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleEmailLogin = async (e: FormEvent) => {
    e.preventDefault();
    if (isLoading) return;
    setError('');

    if (!email.trim() || !password.trim()) {
      setError('Please enter email and password');
      return;
    }

    const result = await loginWithEmail(email, password);
    if (result.success) {
      navigate('/courses');
    } else {
      setError(result.error || 'Invalid email or password');
    }
  };

  const handleGoogleLogin = () => {
    setError('');
    loginWithGoogle();
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    const result = await loginWithMicrosoft();
    if (result.success) {
      navigate('/courses');
    } else {
      setError(result.error || 'Microsoft login failed');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-icon">
            <FlaskConical size={32} />
          </div>
          <h1>BDP vLab</h1>
          <p>Virtual Laboratory Simulations</p>
        </div>

        <form onSubmit={handleEmailLogin} className="login-form">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email address"
            autoFocus
            className="login-input"
            disabled={isLoading}
          />
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="login-input"
            disabled={isLoading}
          />
          {error && <p className="login-error">{error}</p>}
          <button type="submit" className="login-submit primary" disabled={isLoading}>
            {isLoading ? <Loader2 size={18} className="spin" /> : 'Sign In'}
          </button>
        </form>

        <div className="login-divider">
          <span>or continue with</span>
        </div>

        <div className="oauth-buttons">
          <button
            type="button"
            className="oauth-btn google"
            onClick={handleGoogleLogin}
            disabled={isLoading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Sign in with Google
          </button>

          {azureAuthService.isConfigured() && (
            <button
              type="button"
              className="oauth-btn microsoft"
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
            >
              <svg width="18" height="18" viewBox="0 0 21 21">
                <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
              </svg>
              Sign in with Microsoft
            </button>
          )}
        </div>

        <div className="login-footer-links">
          <p>Don't have an account? <Link to="/signup" className="login-link">Sign Up</Link></p>
          <p><Link to="/" className="login-link-secondary">Back to Home</Link></p>
        </div>
      </div>

      <style>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(135deg, var(--bg-dark, #1A1A1A) 0%, var(--bg-surface, #2C2C2C) 50%, var(--bg-elevated, #3A3A3A) 100%);
        }
        .login-card {
          background: var(--bg-surface, #2C2C2C);
          border-radius: 16px;
          padding: 40px;
          width: 420px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
          border: 1px solid var(--border, #4A4A4A);
        }
        .login-header {
          text-align: center;
          margin-bottom: 32px;
        }
        .login-icon {
          width: 64px;
          height: 64px;
          background: linear-gradient(135deg, var(--primary, #FF5A36), var(--secondary, #00A86B));
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 16px;
          color: white;
        }
        .login-header h1 {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 4px 0;
          color: var(--text-primary, #FFFFFF);
        }
        .login-header p {
          color: var(--text-secondary, #CCCCCC);
          font-size: 14px;
          margin: 0;
        }
        .login-form {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .login-input {
          width: 100%;
          padding: 14px 16px;
          border: 2px solid var(--border, #4A4A4A);
          border-radius: 10px;
          background: var(--bg-elevated, #3A3A3A);
          color: var(--text-primary, #FFFFFF);
          font-size: 15px;
          outline: none;
          transition: border-color 0.2s;
          box-sizing: border-box;
        }
        .login-input:focus {
          border-color: var(--primary, #FF5A36);
        }
        .login-input::placeholder {
          color: var(--text-muted, #999999);
        }
        .login-input:disabled {
          opacity: 0.6;
        }
        .login-error {
          color: var(--danger, #EF4444);
          font-size: 13px;
          text-align: center;
          margin: 0;
        }
        .login-submit {
          width: 100%;
          padding: 14px;
          border: none;
          border-radius: 10px;
          font-weight: 600;
          font-size: 15px;
          color: white;
          cursor: pointer;
          transition: transform 0.15s, box-shadow 0.15s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
        .login-submit:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
        }
        .login-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        .login-submit.primary {
          background: linear-gradient(135deg, var(--primary, #FF5A36), var(--primary-dark, #E04420));
        }
        .login-divider {
          display: flex;
          align-items: center;
          gap: 16px;
          margin: 20px 0;
        }
        .login-divider::before,
        .login-divider::after {
          content: '';
          flex: 1;
          height: 1px;
          background: var(--border, #4A4A4A);
        }
        .login-divider span {
          color: var(--text-muted, #999999);
          font-size: 13px;
          white-space: nowrap;
        }
        .oauth-buttons {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .oauth-btn {
          width: 100%;
          padding: 12px;
          border: 2px solid var(--border, #4A4A4A);
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: background 0.2s, border-color 0.2s;
        }
        .oauth-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .oauth-btn.google {
          background: #fff;
          color: #333;
          border-color: #ddd;
        }
        .oauth-btn.google:hover:not(:disabled) {
          background: #f5f5f5;
          border-color: #bbb;
        }
        .oauth-btn.microsoft {
          background: var(--bg-elevated, #3A3A3A);
          color: var(--text-primary, #FFFFFF);
        }
        .oauth-btn.microsoft:hover:not(:disabled) {
          background: #444;
          border-color: #666;
        }
        .login-footer-links {
          text-align: center;
          margin-top: 24px;
        }
        .login-footer-links p {
          font-size: 13px;
          color: rgba(255,255,255,0.4);
          margin: 6px 0;
        }
        .login-link {
          color: #FF7B5C;
          text-decoration: none;
          font-weight: 600;
        }
        .login-link:hover {
          text-decoration: underline;
        }
        .login-link-secondary {
          color: rgba(255,255,255,0.4);
          text-decoration: none;
        }
        .login-link-secondary:hover {
          color: rgba(255,255,255,0.6);
        }
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <Watermark />
      <CopyrightFooter />
    </div>
  );
}
